import {
  BenefitParams,
  CitiesResponse,
  deleteBenefit,
  deleteMultipleBenefits,
  duplicateBenefit,
  getBenefits,
  getBenefitsCities,
  getCircuitsUsingBenefit,
  getCountriesWithBenefits,
  getDraftsBenefits,
  getHotelAssociatedBenefits,
  getPublishedBenefits,
  getSuppliersWithBenefits,
  getTypesWithBenefits,
  HotelAssociatedBenefitsParams,
  HotelAssociatedBenefitsResponse,
  PublishedBenefitsParams,
  PublishedBenefitsResponse,
  type CountriesWithBenefits,
  type SuppliersWithBenefits,
  type TypesWithBenefits,
} from '@/api/benefit.api';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

// Toutes les vues qui affichent des prestations (liste, brouillons, filtres,
// circuits qui les embarquent). À appeler après toute mutation qui crée,
// modifie, publie, duplique ou supprime une prestation : avec le staleTime
// global, une clé oubliée ici reste servie depuis le cache au retour.
export const invalidateBenefitLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.benefits.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.circuits.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.drafts.benefits });
  queryClient.invalidateQueries({ queryKey: queryKeys.countriesBenefits });
  queryClient.invalidateQueries({ queryKey: queryKeys.typesBenefits });
  queryClient.invalidateQueries({ queryKey: queryKeys.suppliersBenefits });
  queryClient.invalidateQueries({ queryKey: queryKeys.trash.all });
};

export const useGetBenefits = (params: BenefitParams) => {
  return useQuery({
    queryKey: queryKeys.benefits.list(params),
    queryFn: () => getBenefits(params),
    placeholderData: keepPreviousData,
  });
};

export const useDeleteBenefit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ benefitId }: { benefitId: string }) =>
      deleteBenefit(benefitId),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
    },
  });
};

export const useDeleteMultipleBenefits = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ benefitIds }: { benefitIds: string[] }) =>
      deleteMultipleBenefits(benefitIds),
    onSuccess: () => {
      invalidateBenefitLists(queryClient);
    },
  });
};

export const useDuplicateBenefit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      benefitId,
      fields,
    }: {
      benefitId: string;
      fields: string[];
    }) => duplicateBenefit(benefitId, fields),
    onSuccess: () => invalidateBenefitLists(queryClient),
  });
};

export const useGetDraftsBenefits = () =>
  useQuery({
    queryKey: queryKeys.drafts.benefits,
    queryFn: () => getDraftsBenefits(),
    placeholderData: keepPreviousData,
  });

export const useGetCountriesWithBenefits = <TData = CountriesWithBenefits>(
  select?: (data: CountriesWithBenefits) => TData,
) =>
  useQuery({
    queryKey: queryKeys.countriesBenefits,
    queryFn: () => getCountriesWithBenefits(),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetTypesWithBenefits = <TData = TypesWithBenefits>(
  select?: (data: TypesWithBenefits) => TData,
) =>
  useQuery({
    queryKey: queryKeys.typesBenefits,
    queryFn: () => getTypesWithBenefits(),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetSuppliersWithBenefits = <TData = SuppliersWithBenefits>(
  select?: (data: SuppliersWithBenefits) => TData,
) =>
  useQuery({
    queryKey: queryKeys.suppliersBenefits,
    queryFn: () => getSuppliersWithBenefits(),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetPublishedBenefits = <TData = PublishedBenefitsResponse>(
  params: PublishedBenefitsParams,
  select?: (data: PublishedBenefitsResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.benefits.published(params),
    queryFn: () => getPublishedBenefits(params),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetHotelAssociatedBenefits = <
  TData = HotelAssociatedBenefitsResponse,
>(
  params: HotelAssociatedBenefitsParams,
  select?: (data: HotelAssociatedBenefitsResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.benefits.hotelAssociated(params),
    queryFn: () => getHotelAssociatedBenefits(params),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetBenefitsCities = <TData = CitiesResponse>({
  country,
  select,
}: {
  country?: string;
  select?: (data: CitiesResponse) => TData;
}) =>
  useQuery({
    queryKey: queryKeys.benefits.cities(country),
    queryFn: () => getBenefitsCities(country),
    select,
  });

export const useGetCircuitsUsingBenefit = (benefitId: string) =>
  useQuery({
    queryKey: queryKeys.benefits.circuits(benefitId),
    queryFn: () => getCircuitsUsingBenefit(benefitId),
  });
